/**
 * The staff time clock: clock in and out, take a break, see this week.
 *
 * The browser never sends a punch time. The database stamps every punch, so
 * a wrong clock on a classroom tablet cannot move anyone's hours.
 */

import { useEffect, useState } from "react";
import { useSession } from "../auth/SessionProvider";
import { Button, Card, Empty, Notice, PageHeader, Tag } from "../components/ui";
import { describeApiError } from "../lib/api";
import { formatDay, formatDuration, formatTime } from "../lib/format";
import { useLoad } from "../lib/useLoad";

export interface TimeEntry {
  id: string;
  clockIn: string;
  clockOut: string | null;
  breakMinutes: number;
  paidMinutes: number | null;
  note: string | null;
  edited: boolean;
  approvedAt: string | null;
}

export interface ClockStatus {
  timezone: string;
  today: string;
  weekStart: string;
  open: TimeEntry | null;
  openBreak: { startedAt: string; paid: boolean } | null;
  weekClosedMinutes: number;
  entries: TimeEntry[];
}

function minutesSince(iso: string, now: number) {
  return Math.max(0, Math.floor((now - Date.parse(iso)) / 60000));
}

export function useClockStatus() {
  const { api } = useSession();
  return useLoad(() => api<ClockStatus>("/api/timeclock/status"), [api]);
}

/** The clock-in card, also shown on the Today page. */
export function ClockCard({ status, onChange }: { status: ClockStatus; onChange: () => void }) {
  const { api } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  async function act(path: string, body?: object) {
    setBusy(true);
    setError(null);
    try {
      await api(path, { method: "POST", body: JSON.stringify(body ?? {}) });
      onChange();
    } catch (caught) {
      setError(describeApiError(caught));
    } finally {
      setBusy(false);
    }
  }

  const { open, openBreak, timezone } = status;
  const runningMinutes = open ? minutesSince(open.clockIn, now) : 0;

  return (
    <Card>
      {error ? (
        <div className="mb-4">
          <Notice>{error}</Notice>
        </div>
      ) : null}

      {!open ? (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-lg font-semibold">You are clocked out.</p>
            <p className="text-sm text-stone-600">
              {formatDuration(status.weekClosedMinutes)} worked since {formatDay(status.weekStart)}.
            </p>
          </div>
          <Button size="lg" disabled={busy} onClick={() => void act("/api/timeclock/clock-in")}>
            {busy ? "Clocking in" : "Clock in"}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-lg font-semibold">
                {openBreak ? "On break" : "Clocked in"} since{" "}
                {formatTime(openBreak ? openBreak.startedAt : open.clockIn, timezone)}
              </p>
              <p className="text-sm text-stone-600">
                {formatDuration(runningMinutes)} on this shift
                {openBreak ? `, break running ${formatDuration(minutesSince(openBreak.startedAt, now))}` : ""}.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {openBreak ? (
                <Button variant="secondary" disabled={busy} onClick={() => void act("/api/timeclock/break/end")}>
                  End break
                </Button>
              ) : (
                <Button variant="secondary" disabled={busy} onClick={() => void act("/api/timeclock/break/start")}>
                  Start break
                </Button>
              )}
              <Button disabled={busy} onClick={() => void act("/api/timeclock/clock-out")}>
                {busy ? "Saving" : "Clock out"}
              </Button>
            </div>
          </div>
          {openBreak ? <p className="text-sm text-stone-500">Clocking out ends the break too.</p> : null}
        </div>
      )}
    </Card>
  );
}

export function TimeClock() {
  const { data, error, reload } = useClockStatus();

  return (
    <div>
      <PageHeader title="Time clock" detail={data ? `Times are shown in ${data.timezone.replace(/_/g, " ")}.` : undefined} />

      {error ? <Notice>{error}</Notice> : null}

      {data ? (
        <div className="space-y-6">
          <ClockCard status={data} onChange={reload} />

          <Card>
            <h2 className="text-lg font-semibold">This week</h2>
            {data.entries.length === 0 ? (
              <div className="mt-4">
                <Empty>No time recorded since {formatDay(data.weekStart)}.</Empty>
              </div>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[520px] text-left text-sm">
                  <thead className="text-stone-500">
                    <tr>
                      <th className="pb-2 font-medium">Day</th>
                      <th className="pb-2 font-medium">In</th>
                      <th className="pb-2 font-medium">Out</th>
                      <th className="pb-2 font-medium">Break</th>
                      <th className="pb-2 font-medium">Paid</th>
                      <th className="pb-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {data.entries.map((entry) => (
                      <tr key={entry.id}>
                        <td className="py-2.5">
                          {new Intl.DateTimeFormat("en-US", { timeZone: data.timezone, weekday: "short", month: "short", day: "numeric" }).format(new Date(entry.clockIn))}
                        </td>
                        <td className="py-2.5">{formatTime(entry.clockIn, data.timezone)}</td>
                        <td className="py-2.5">{entry.clockOut ? formatTime(entry.clockOut, data.timezone) : "Open"}</td>
                        <td className="py-2.5">{entry.breakMinutes ? formatDuration(entry.breakMinutes) : ""}</td>
                        <td className="py-2.5">{formatDuration(entry.paidMinutes)}</td>
                        <td className="space-x-1 py-2.5 text-right">
                          {entry.edited ? <Tag tone="warn">Edited</Tag> : null}
                          {entry.approvedAt ? <Tag tone="ok">Approved</Tag> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-4 text-sm text-stone-500">
              If a punch is wrong, tell a director. They can fix it, and the change shows here as edited.
            </p>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
